import { useGetActivityMetricsQuery } from 'api/recoveryAi'
import useRecoveryAiOn from 'hooks/useRecoveryAiOn'
import { useSelector } from 'react-redux'
import { selectCurrentMerchant } from 'selectors/merchant'
import {
  RecoveryAiCoverage,
  resolveSetupCardState,
  SetupCardState,
} from '../setupCardState'
import { useWrittenFeedbackPersisted } from './useWrittenFeedbackEnabled'

// Gathers the two inputs the "Your current setup" card needs and hands them
// to the resolver. The DECISION is not here — it is in setupCardState.ts, the
// one module AI-1257 replaces. This hook only fetches.
//
// `src/components/` may not fetch, so the fetching lives here and the card
// itself takes a plain union prop.
//
// SEAM, and a divergence to close. The N of M this card renders comes from
// `recovery/activities/metrics`, which counts CONFIG ROWS. The Overview page's
// "N of M locations on" badge reads `recovery/location_configs/summary`
// instead, which COALESCEs a location with no row to the merchant default — so
// the two surfaces can report different denominators for the same merchant.
// The summary endpoint is not deployed yet (thanx/nexus#1943, open; see
// api/recoveryAi.ts), so switching now would trade a working count for
// "coverage unknown". Move this hook onto the summary query once #1943 ships.
//
// Returns `null` while the inputs are still settling — see the early return
// below. The loading decision stays HERE, not in setupCardState.ts, so AI-1257's
// replacement resolver still drops into that one module unchanged.
export const useSetupCardState = (): SetupCardState | null => {
  // Input 1 — the written-feedback setting AS SAVED, not as currently toggled.
  // The card describes what guests are getting right now, so it must not react
  // to an edit the merchant has not published: it appears only after Publish
  // turns collection on, and stays until Publish turns it off.
  //
  // Hydrates the slice too, so the card is correct on a deep link straight into
  // this sub-tab. That is not a second fetch: the initialize hook projects the
  // already-loaded current merchant out of redux-orm and is idempotent.
  const writtenFeedbackEnabled = useWrittenFeedbackPersisted()

  // ...and whether that value can be trusted yet. The slice seeds it TRUE
  // (slices/orderFeedbackCollection/initialState.ts, deliberately, so the
  // off-state amber note does not flash), and `selectCurrentMerchant` answers
  // `{} as Merchant` rather than undefined while the ORM has no row — so the
  // initialize hook's `if (merchant)` passes on the very first render and
  // hydrates the slice to TRUE for every merchant, including one that has
  // collection off. The merchant's id is the only honest "it is really here"
  // signal, so read it directly rather than trusting the seeded value.
  const merchantLoaded = useSelector(selectCurrentMerchant)?.id !== undefined

  // Input 2 — RecoveryAI's N of M. Skipped exactly the way Home's
  // NeedsAttention skips it: `recovery/activities/metrics` is gated server-side
  // by `require_recovery_ai!` plus the `merchant_settings` view permission, so
  // a merchant without RecoveryAI (or a viewer without that permission) would
  // otherwise trade a guaranteed 403 for nothing. `useRecoveryAiOn` is the
  // repo's single "RecoveryAI is live AND this viewer can reach it" predicate;
  // this is a request guard, not a feature gate on the card.
  const recoveryAiOn = useRecoveryAiOn()
  const {
    data: metrics,
    isLoading: metricsLoading,
    isUninitialized: metricsUninitialized,
  } = useGetActivityMetricsQuery(undefined, {
    skip: !recoveryAiOn,
  })

  // NOT ANSWERED YET is not the same fact as NOT CONFIGURED, and the resolver
  // cannot tell them apart: both reach it as `null`. Without this gate a
  // merchant on RecoveryAI is told "Collecting feedback with Thanx" for two
  // sequential round trips — `recovery/config` has to resolve before
  // `useRecoveryAiOn` turns true and unskips the metrics request — and the card
  // then rewrites itself in front of them.
  //
  // `isUninitialized` is load-bearing alongside `isLoading`: while the metrics
  // query is still skipped it reports uninitialized, not loading. It only
  // counts while `recoveryAiOn` is true, because a merchant WITHOUT RecoveryAI
  // leaves that query skipped forever and would otherwise never settle.
  //
  // The merchant is the other not-settled input, for the same reason: until it
  // lands, `writtenFeedbackEnabled` is the slice's seeded TRUE and not the
  // merchant's own value, so a merchant with collection OFF would get the full
  // card on first paint and lose it a moment later.
  //
  // Answering `null` rather than a state: the card describes what guests are
  // getting, and until these resolve there is no honest answer to give. The
  // pane renders nothing in the window.
  const settling =
    !merchantLoaded ||
    (recoveryAiOn && (metricsUninitialized || metricsLoading))

  if (settling) return null

  // Every failure mode collapses to `null`, and the resolver then picks a
  // non-RecoveryAI state: a skipped query, a 403, a null body, and a response
  // that omits `active_locations_count` all land here. The card renders in all
  // of them — no spinner, no error, and never "undefined of undefined".
  const totalLocations = metrics?.totalLocationsCount
  const activeLocations = metrics?.activeLocationsCount
  const recoveryAi: RecoveryAiCoverage | null =
    typeof totalLocations === 'number' && typeof activeLocations === 'number'
      ? { totalLocations, activeLocations }
      : null

  return resolveSetupCardState({ writtenFeedbackEnabled, recoveryAi })
}
